<script setup>
import { ref, computed } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { messageStamp } from 'shared/helpers/timeHelper';

const props = defineProps({
  segments: { type: Array, default: () => [] },
  timelineStart: { type: Number, required: true },
  timelineEnd: { type: Number, required: true },
  timelineTicks: { type: Array, default: () => [] },
  userId: { type: [String, Number], required: true },
  canMovePrevious: Boolean,
  canMoveNext: Boolean,
  selectedScale: { type: String, default: 'daily' },
  availableScales: { type: Array, default: () => [] },
  displayTotals: {
    type: Object,
    default: () => ({ accepting: 0, not_accepting: 0, logged_out: 0 }),
  },
});

const emit = defineEmits(['previous', 'next', 'reset', 'scaleChange']);

const { t } = useI18n();

const tooltip = ref(null);
const wrapperRef = ref(null);
const scaleMenuOpen = ref(false);
const scaleMenuRef = ref(null);

const selectedScaleLabel = computed(() => {
  const match = props.availableScales.find(
    scale => scale.key === props.selectedScale
  );
  return match?.label || props.selectedScale;
});

function toggleScaleMenu() {
  scaleMenuOpen.value = !scaleMenuOpen.value;
}

function chooseScale(key) {
  scaleMenuOpen.value = false;
  if (key !== props.selectedScale) emit('scaleChange', key);
}

onClickOutside(scaleMenuRef, () => {
  scaleMenuOpen.value = false;
});

function showTooltip(event, segment) {
  if (!wrapperRef.value) return;
  const rect = wrapperRef.value.getBoundingClientRect();
  tooltip.value = {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top - 52,
    segment,
  };
}

function hideTooltip() {
  tooltip.value = null;
}

const availabilityMap = {
  online: 'ACCEPTING',
  busy: 'NOT_ACCEPTING',
  offline: 'LOGGED_OUT',
};

function availabilityLabel(apiKey) {
  const key = availabilityMap[String(apiKey || '').toLowerCase()];
  return key ? t(`AGENT_ACTIVITY_REPORT.STATUSES.${key}`) : '—';
}

function availabilityColor(status) {
  if (status === 'online') return '#22c55e';
  if (status === 'busy') return '#eab308';
  return '#64748b';
}

const formatDuration = totalSeconds => {
  const seconds = Number(totalSeconds || 0);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m`;
};

const timelineDuration = computed(() =>
  Math.max(1, props.timelineEnd - props.timelineStart)
);

const displaySegments = computed(() =>
  props.segments.map(segment => {
    const segStart = Number(segment.from || props.timelineStart);
    const segEnd = Number(segment.to || segStart);
    const left =
      ((segStart - props.timelineStart) / timelineDuration.value) * 100;
    const width = ((segEnd - segStart) / timelineDuration.value) * 100;
    return {
      ...segment,
      left: Math.max(0, Math.min(left, 100)),
      width: Math.max(0.1, Math.min(width, 100)),
      color: availabilityColor(segment.availability),
    };
  })
);

function tickLabelStyle(tick, index, total) {
  if (index === 0) return { left: '0%', transform: 'translateX(0)' };
  if (index === total - 1)
    return { left: '100%', transform: 'translateX(-100%)' };
  return { left: `${tick.x}%`, transform: 'translateX(-50%)' };
}

const tooltipData = computed(() => {
  if (!tooltip.value) return null;
  const s = tooltip.value.segment;
  return {
    status: availabilityLabel(s.availability),
    range: `${messageStamp(s.from, 'dd/MM/yy HH:mm')} – ${messageStamp(s.to, 'dd/MM/yy HH:mm')}`,
    duration: formatDuration(s.duration),
    color: availabilityColor(s.availability),
  };
});

const animKey = computed(() => `${props.userId}-${props.timelineStart}`);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <span
        class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
      >
        {{ $t('AGENT_ACTIVITY_REPORT.TIMELINE.TITLE') }}
      </span>
      <div class="flex h-8 shrink-0 items-center gap-1.5">
        <div ref="scaleMenuRef" class="relative">
          <button
            type="button"
            class="inline-flex h-8 min-w-[6.5rem] items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-xs text-n-slate-12 transition-colors hover:bg-n-alpha-3 dark:bg-n-solid-3"
            :aria-expanded="scaleMenuOpen"
            :aria-label="$t('AGENT_ACTIVITY_REPORT.SCALE.LABEL')"
            @click.stop="toggleScaleMenu"
          >
            <span>{{ selectedScaleLabel }}</span>
            <span
              class="i-lucide-chevron-down size-3.5 shrink-0 text-n-slate-11"
            />
          </button>
          <div
            v-if="scaleMenuOpen"
            class="absolute right-0 z-20 mt-1 min-w-full overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 py-1 shadow-lg dark:bg-n-solid-3"
          >
            <button
              v-for="scale in availableScales"
              :key="scale.key"
              type="button"
              class="flex w-full items-center px-3 py-1.5 text-left text-xs text-n-slate-12 transition-colors hover:bg-n-alpha-3"
              :class="{
                'font-medium text-n-brand': scale.key === selectedScale,
              }"
              @click.stop="chooseScale(scale.key)"
            >
              {{ scale.label }}
            </button>
          </div>
        </div>
        <button
          type="button"
          class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 transition-colors hover:bg-n-alpha-3 disabled:cursor-not-allowed disabled:opacity-30 dark:bg-n-solid-3"
          :disabled="!canMovePrevious"
          :title="$t('AGENT_ACTIVITY_REPORT.NAV.PREVIOUS')"
          @click="emit('previous')"
        >
          <span class="i-lucide-chevron-left size-3.5" />
        </button>
        <button
          type="button"
          class="inline-flex h-8 shrink-0 items-center justify-center rounded-lg border border-n-weak bg-n-solid-1 px-3 text-xs text-n-slate-12 transition-colors hover:bg-n-alpha-3 dark:bg-n-solid-3"
          @click="emit('reset')"
        >
          {{ $t('AGENT_ACTIVITY_REPORT.NAV.LATEST') }}
        </button>
        <button
          type="button"
          class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 transition-colors hover:bg-n-alpha-3 disabled:cursor-not-allowed disabled:opacity-30 dark:bg-n-solid-3"
          :disabled="!canMoveNext"
          :title="$t('AGENT_ACTIVITY_REPORT.NAV.NEXT')"
          @click="emit('next')"
        >
          <span class="i-lucide-chevron-right size-3.5" />
        </button>
      </div>
    </div>

    <div
      ref="wrapperRef"
      class="relative select-none rounded-lg border border-n-weak bg-n-solid-1 px-3 pb-2 pt-4 dark:bg-n-solid-3"
    >
      <svg
        :key="animKey"
        viewBox="0 0 100 10"
        class="block h-6 w-full overflow-visible"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <rect
          x="0"
          y="3"
          width="100"
          height="4"
          fill="#cbd5e1"
          class="dark:fill-slate-700"
        />
        <line
          v-for="tick in timelineTicks"
          :key="`${userId}-t-${tick.value}`"
          :x1="tick.x"
          y1="1"
          :x2="tick.x"
          y2="9"
          stroke="#94a3b8"
          stroke-width="0.25"
        />
        <g
          v-for="segment in displaySegments"
          :key="`${userId}-s-${segment.from}-${segment.to}`"
          class="cursor-pointer"
          @mouseenter="showTooltip($event, segment)"
          @mouseleave="hideTooltip"
        >
          <rect
            :x="segment.left"
            y="3"
            :width="segment.width"
            height="4"
            :fill="segment.color"
          />
        </g>
      </svg>

      <div class="relative mt-2 h-4">
        <span
          v-for="(tick, index) in timelineTicks"
          :key="`${userId}-l-${tick.value}`"
          class="absolute top-0 whitespace-nowrap text-[10px] leading-tight text-n-slate-10"
          :style="tickLabelStyle(tick, index, timelineTicks.length)"
        >
          {{ tick.label }}
        </span>
      </div>

      <Transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="opacity-0 translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-1"
      >
        <div
          v-if="tooltip && tooltipData"
          class="pointer-events-none absolute z-50 -translate-x-1/2 whitespace-nowrap rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 shadow-lg"
          :style="{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }"
        >
          <div class="mb-0.5 flex items-center gap-1.5">
            <span
              class="h-1.5 w-1.5 shrink-0 rounded-full"
              :style="{ backgroundColor: tooltipData.color }"
            />
            <span
              class="text-[11px] font-semibold"
              :style="{ color: tooltipData.color }"
            >
              {{ tooltipData.status }}
            </span>
          </div>
          <div class="text-[11px] text-n-slate-11">{{ tooltipData.range }}</div>
          <div class="mt-0.5 text-[11px] font-medium text-n-slate-12">
            {{ tooltipData.duration }}
          </div>
        </div>
      </Transition>
    </div>

    <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-n-slate-11">
      <span class="flex items-center gap-1.5">
        <span class="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
        {{ $t('AGENT_ACTIVITY_REPORT.TOTALS.ACCEPTING') }}
        <span class="font-semibold text-emerald-600 dark:text-emerald-400">{{
          formatDuration(displayTotals.accepting)
        }}</span>
      </span>
      <span class="flex items-center gap-1.5">
        <span class="h-2 w-2 shrink-0 rounded-full bg-amber-500" />
        {{ $t('AGENT_ACTIVITY_REPORT.TOTALS.NOT_ACCEPTING') }}
        <span class="font-semibold text-amber-600 dark:text-amber-400">{{
          formatDuration(displayTotals.not_accepting)
        }}</span>
      </span>
      <span class="flex items-center gap-1.5">
        <span class="h-2 w-2 shrink-0 rounded-full bg-slate-500" />
        {{ $t('AGENT_ACTIVITY_REPORT.TOTALS.LOGGED_OUT') }}
        <span class="font-semibold text-slate-500 dark:text-slate-400">{{
          formatDuration(displayTotals.logged_out)
        }}</span>
      </span>
    </div>
  </div>
</template>
